import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { AppShell3 } from '@/components/app-shell-3';
import { ToolHeader } from '@/components/ToolHeader';
import { PageTransition } from '@/components/PageTransition';
import { useSpaceData } from '@/hooks/useSpaceData';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { supabase } from '@/lib/supabase';
import {
  Clock,
  Play,
  Pause,
  Pencil,
  Trash2,
  MoreHorizontal,
  GitBranch,
  CheckCircle2,
  XCircle,
  Calendar,
  ArrowLeft,
  Eye,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';

export default function ScheduleSummaryPage() {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const scheduleId = searchParams.get('id');
  const { userProfile, company } = useAuth();
  // Pass company.id to validate the space belongs to the user's current company
  const { space, spaceTools, loading, accessDenied } = useSpaceData(projectId, userProfile?.id, company?.id);

  // Set page title
  usePageTitle('Schedule Summary');

  // Redirect if user doesn't have access (e.g., switched companies)
  useEffect(() => {
    if (accessDenied) {
      toast.error('You no longer have access to this space');
      navigate('/dashboard', { replace: true });
    }
  }, [accessDenied, navigate]);

  const [schedule, setSchedule] = useState(null);
  const [runHistory, setRunHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isTogglingActive, setIsTogglingActive] = useState(false);

  // Load schedule and run history
  useEffect(() => {
    const loadData = async () => {
      if (!scheduleId) {
        navigate(projectId ? `/space/${projectId}/tasks?view=schedules` : '/tasks?view=schedules');
        return;
      }

      try {
        setIsLoading(true);

        // Fetch schedule details
        const { data: scheduleData, error: scheduleError } = await supabase
          .from('workflow_schedules')
          .select(`
            *,
            workflow:workflows(id, name, description, settings),
            creator:user_profiles!created_by(id, first_name, last_name, avatar_url)
          `)
          .eq('id', scheduleId)
          .single();

        if (scheduleError) throw scheduleError;
        setSchedule(scheduleData);

        // Fetch run history - workflow runs triggered by this schedule
        // We'll match by workflow_id and trigger_type='schedule' and created after schedule was created
        if (scheduleData?.workflow_id) {
          const { data: runsData, error: runsError } = await supabase
            .from('checklists_workflow_runs')
            .select(`
              id,
              run_name,
              status,
              started_at,
              completed_at,
              trigger_type,
              created_date,
              task_runs:checklists_workflow_task_runs(id, status)
            `)
            .eq('workflow_id', scheduleData.workflow_id)
            .eq('trigger_type', 'schedule')
            .gte('created_date', scheduleData.created_date)
            .order('created_date', { ascending: false })
            .limit(50);

          if (runsError) throw runsError;
          setRunHistory(runsData || []);
        }
      } catch (error) {
        console.error('Error loading schedule:', error);
        toast.error('Failed to load schedule');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [scheduleId, projectId, navigate]);

  const handleToggleActive = async () => {
    if (!schedule) return;

    try {
      setIsTogglingActive(true);
      const newStatus = !schedule.is_active;

      const { error } = await supabase
        .from('workflow_schedules')
        .update({ is_active: newStatus })
        .eq('id', schedule.id);

      if (error) throw error;

      setSchedule({ ...schedule, is_active: newStatus });
      toast.success(newStatus ? 'Schedule activated' : 'Schedule paused');
    } catch (error) {
      console.error('Error toggling schedule:', error);
      toast.error('Failed to update schedule');
    } finally {
      setIsTogglingActive(false);
    }
  };

  const handleDelete = async () => {
    if (!schedule) return;
    if (!confirm('Are you sure you want to delete this schedule? This cannot be undone.')) return;

    try {
      const { error } = await supabase
        .from('workflow_schedules')
        .delete()
        .eq('id', schedule.id);

      if (error) throw error;

      toast.success('Schedule deleted');
      navigate(projectId ? `/space/${projectId}/tasks?view=schedules` : '/tasks?view=schedules');
    } catch (error) {
      console.error('Error deleting schedule:', error);
      toast.error('Failed to delete schedule');
    }
  };

  const handleRunNow = async () => {
    if (!schedule?.workflow_id) return;

    try {
      // Trigger manual run by calling the execute-schedules function with this schedule
      // For now, we'll update next_run_at to now to trigger it on next cron run
      const { error } = await supabase
        .from('workflow_schedules')
        .update({ next_run_at: new Date().toISOString() })
        .eq('id', schedule.id);

      if (error) throw error;

      toast.success('Schedule will run shortly');
    } catch (error) {
      console.error('Error triggering run:', error);
      toast.error('Failed to trigger run');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'in_progress':
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200"><Clock className="w-3 h-3 mr-1" /> In Progress</Badge>;
      case 'completed':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200"><CheckCircle2 className="w-3 h-3 mr-1" /> Completed</Badge>;
      case 'cancelled':
        return <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200"><XCircle className="w-3 h-3 mr-1" /> Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getRunProgress = (run) => {
    const taskRuns = run.task_runs || [];
    const completed = taskRuns.filter(t => t.status === 'completed').length;
    const total = taskRuns.length;
    return { completed, total };
  };

  const getFrequencyDisplay = () => {
    if (!schedule) return '';

    let freq = schedule.frequency.charAt(0).toUpperCase() + schedule.frequency.slice(1);

    if (schedule.frequency === 'weekly' && schedule.days_of_week?.length > 0) {
      const days = schedule.days_of_week.map(d => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d]).join(', ');
      freq += ` (${days})`;
    } else if (schedule.frequency === 'monthly' && schedule.day_of_month) {
      freq += ` (Day ${schedule.day_of_month})`;
    }

    return `${freq} at ${schedule.time?.slice(0, 5)}`;
  };

  if (loading || isLoading) {
    return (
      <AppShell3 projectId={projectId} activeTool="tasks">
        <PageTransition>
          <div className="min-h-screen flex items-center justify-center">
            <LoadingSpinner />
          </div>
        </PageTransition>
      </AppShell3>
    );
  }

  if (!schedule) {
    return (
      <AppShell3 projectId={projectId} activeTool="tasks">
        <PageTransition>
          <div className="min-h-screen flex items-center justify-center">
            <div className="text-center">
              <Clock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">Schedule not found</h3>
              <Button onClick={() => navigate(projectId ? `/space/${projectId}/tasks?view=schedules` : '/tasks?view=schedules')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Library
              </Button>
            </div>
          </div>
        </PageTransition>
      </AppShell3>
    );
  }

  return (
    <AppShell3 projectId={projectId} activeTool="tasks">
      <ToolHeader
        spaceName={space?.name || 'Space'}
        spaceImage={space?.logo_url}
        toolName={schedule.name}
        currentTool="tasks"
        spaceTools={spaceTools}
        parentBreadcrumb={{
          name: 'Schedules',
          onClick: () => {
            navigate(projectId ? `/space/${projectId}/tasks?view=schedules` : '/tasks?view=schedules');
          }
        }}
        actions={
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Label htmlFor="schedule-active" className="text-sm text-muted-foreground">
                {schedule.is_active ? 'Active' : 'Paused'}
              </Label>
              <Switch
                id="schedule-active"
                checked={schedule.is_active}
                onCheckedChange={handleToggleActive}
                disabled={isTogglingActive}
              />
            </div>
            <div className="w-px h-6 bg-border" />
            <Button variant="outline" size="sm" onClick={handleRunNow} disabled={!schedule.is_active}>
              <Play className="h-4 w-4 mr-1.5" />
              Run Now
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="h-9 w-9">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => navigate(projectId
                  ? `/space/${projectId}/schedule-editor?id=${schedule.id}`
                  : `/tasks/schedule-editor?id=${schedule.id}`
                )}>
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit Schedule
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive"
                  onClick={handleDelete}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Schedule
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        }
      />
      <PageTransition>
        <div className="p-6 space-y-6 max-w-5xl mx-auto">
          {/* Schedule Header Card */}
          <div className="bg-card border rounded-lg p-6">
            <div className="flex items-start gap-4">
              <div className={`h-14 w-14 rounded-full flex items-center justify-center ${
                schedule.is_active ? 'bg-green-100 text-green-600' : 'bg-muted text-muted-foreground'
              }`}>
                <Clock className="h-7 w-7" />
              </div>
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <h1 className="text-2xl font-semibold">{schedule.name}</h1>
                  {schedule.is_active ? (
                    <Badge className="bg-green-100 text-green-700 border-green-200">Active</Badge>
                  ) : (
                    <Badge variant="secondary">Paused</Badge>
                  )}
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <GitBranch className="h-4 w-4" />
                  <span>{schedule.workflow?.name || 'Unknown workflow'}</span>
                </div>
              </div>
            </div>

            {/* Schedule Details Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Frequency</p>
                <p className="font-medium">{getFrequencyDisplay()}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Next Run</p>
                <p className="font-medium">
                  {schedule.next_run_at
                    ? format(new Date(schedule.next_run_at), 'MMM d, yyyy HH:mm')
                    : 'Not scheduled'}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Last Run</p>
                <p className="font-medium">
                  {schedule.last_run_at
                    ? formatDistanceToNow(new Date(schedule.last_run_at), { addSuffix: true })
                    : 'Never'}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Total Runs</p>
                <p className="font-medium">{schedule.run_count || 0}</p>
              </div>
            </div>
          </div>

          {/* Run History */}
          <div className="bg-card border rounded-lg">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold">Run History</h2>
              <p className="text-sm text-muted-foreground">{runHistory.length} runs</p>
            </div>

            {runHistory.length === 0 ? (
              <div className="p-8 text-center">
                <RefreshCw className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
                <h3 className="font-medium mb-1">No runs yet</h3>
                <p className="text-sm text-muted-foreground">
                  This schedule hasn't triggered any workflow runs yet.
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Run Name</TableHead>
                    <TableHead>Started</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Progress</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {runHistory.map((run) => {
                    const progress = getRunProgress(run);

                    return (
                      <TableRow
                        key={run.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => navigate(`/space/${projectId}/workflow-run/${run.id}`)}
                      >
                        <TableCell className="font-medium">
                          {run.run_name || 'Untitled Run'}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {run.started_at
                            ? format(new Date(run.started_at), 'MMM d, yyyy HH:mm')
                            : '-'}
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(run.status)}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {progress.completed}/{progress.total} tasks
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/space/${projectId}/workflow-run/${run.id}`);
                            }}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>
        </div>
      </PageTransition>
    </AppShell3>
  );
}
